import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Spinner, Alert, Badge } from "react-bootstrap";
import { useApiClient } from "../../ApiService/useApiClient";
import { carePlansApi } from "../../ApiService/carePlansApi";

const safeArr = (v) => (Array.isArray(v) ? v : []);

const normalizePlan = (p) => {
  if (!p) return null;
  const id = p.Id ?? p.id ?? p.CarePlanId ?? p.carePlanId;
  const title = p.Title ?? p.title ?? "";
  const status = p.Status ?? p.status ?? "";
  const effectiveFrom = p.EffectiveFrom ?? p.effectiveFrom ?? null;
  const nextReviewOn = p.NextReviewOn ?? p.nextReviewOn ?? null;
  const version = p.Version ?? p.version ?? 1;
  const summary = p.Summary ?? p.summary ?? "";
  const sectionsRaw = p.Sections ?? p.sections ?? [];
  const sections = safeArr(sectionsRaw).map((s) => ({
    type: (s.Type ?? s.type ?? "").toLowerCase(),
    content: s.Content ?? s.content ?? "",
    risks: safeArr(s.Risks ?? s.risks),
  }));
  return { id, title, status, effectiveFrom, nextReviewOn, version, summary, sections };
};

const normalizeCustomer = (c) => {
  if (!c) return null;
  return {
    fullName: c.FullName || c.fullName || "",
    fullAddress: c.FullAddress || c.fullAddress || "",
    dateOfBirth: c.DateOfBirth || c.dateOfBirth || null,
  };
};

const fmt = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "-");

const fmtDob = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

export default function CarePlanPrintView() {
  const { planId } = useParams();
  const api = useApiClient();
  const apiWrap = useMemo(() => carePlansApi(api), [api]);

  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await apiWrap.getById(planId);
        const responseData = res.data;
        
        // Handle new API response structure - check both uppercase and lowercase
        const carePlanData = responseData.CarePlan || responseData.carePlan || responseData;
        const customerData = responseData.Customer || null;
        
        const normalizedPlan = normalizePlan(carePlanData);
        const normalizedCustomer = normalizeCustomer(customerData);
        
        if (alive) {
          setPlan(normalizedPlan);
          setCustomer(normalizedCustomer);
        }
      } catch (e) {
        if (alive) setError(e);
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [apiWrap, planId]);

  useEffect(() => {
    if (!loading && plan) {
      // auto open print dialog
      setTimeout(() => window.print(), 200);
    }
  }, [loading, plan]);

  if (loading) return <div className="p-4"><Spinner animation="border" /></div>;
  if (error) return <div className="p-4"><Alert variant="danger">Failed to load care plan.</Alert></div>;
  if (!plan) return <div className="p-4"><Alert variant="warning">Not found.</Alert></div>;

  return (
    <div className="careplan-print p-4">
      <style>{`
        @media print {
          body { background: #fff !important; }
          .careplan-print { padding: 20px !important; max-width: 100% !important; }
          .no-print { display: none !important; }
        }
        .cp-title { font-size: 24px; font-weight: 800; margin: 0; color: #2c3e50; }
        .cp-meta { margin-top: 8px; color: #555; font-size: 12px; }
        .cp-section { margin-top: 24px; page-break-inside: avoid; }
        .cp-section h3 { font-size: 16px; font-weight: 800; margin-bottom: 12px; color: #2c3e50; }
        .cp-box { border: 1px solid #ddd; border-radius: 8px; padding: 16px; background: #fff; }
        .cp-muted { color: #555; font-size: 12px; }
        .cp-badges { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
        .customer-header { background: #f8f9fa; border: 1px solid #e9ecef; }
        @page {
          margin: 20mm;
          size: A4;
        }
      `}</style>

      <div className="no-print mb-3">
        <button className="btn btn-primary me-2" onClick={() => window.print()}>Print / Save as PDF</button>
        <button className="btn btn-secondary" onClick={() => window.close()}>Close</button>
      </div>

      {/* Customer Information Header */}
      {customer && (
        <div className="mb-4 p-3 border rounded customer-header">
          <div className="d-flex align-items-start gap-3">
            <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px", fontSize: "20px", fontWeight: "bold" }}>
              {customer.fullName ? customer.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'C'}
            </div>
            <div className="flex-grow-1">
              <h3 className="mb-1">{customer.fullName || "Customer"}</h3>
              <div className="small text-muted mb-1">
                <strong>Address:</strong> {customer.fullAddress || "Address not available"}
              </div>
              <div className="small text-muted">
                <strong>Date of Birth:</strong> {fmtDob(customer.dateOfBirth)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Care Plan Title */}
      <div className="text-center mb-4">
        <h1 className="cp-title">{plan.title}</h1>
        <div className="cp-meta">
          Status: <Badge bg="secondary">{plan.status}</Badge>{" "}
          | Version: {plan.version} | Effective: {fmt(plan.effectiveFrom)} | Next review: {fmt(plan.nextReviewOn)}
        </div>
      </div>

      <div className="cp-section">
        <h3>Summary</h3>
        <div className="cp-box">{plan.summary || <span className="cp-muted">No summary.</span>}</div>
      </div>

      {plan.sections.map((s) => (
        <div key={s.type} className="cp-section">
          <h3>{String(s.type).replace(/_/g, " ")}</h3>
          <div className="cp-box">
            {s.content ? <div dangerouslySetInnerHTML={{ __html: s.content }} /> : <span className="cp-muted">No content.</span>}
            {s.risks?.length ? (
              <div className="cp-badges">
                {s.risks.map((r) => (
                  <span key={r} className="badge bg-warning text-dark">{r}</span>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ))}

      {/* Footer */}
      <div className="mt-4 pt-3 border-top text-center">
        <div className="small text-muted">
          Generated on {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} at {new Date().toLocaleTimeString()}
        </div>
        <div className="small text-muted">
          HMCarePlanner - Care Plan Management System
        </div>
      </div>
    </div>
  );
}
